/**
 * ProjectSnapshotsPage — liste de tous les snapshots du projet (tous rapports
 * confondus). Accès via l'entrée de nav « Snapshots ». Consultation + suppression.
 */
import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Camera, Trash2, Eye, Link2 } from 'lucide-react'
import { listProjectSnapshots, deleteSnapshot } from '../features/snapshots/snapshotsApi'
import type { ProjectSnapshot } from '../types/snapshot'

export default function ProjectSnapshotsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const clientId = Number(projectId)
  const [snapshots, setSnapshots] = useState<ProjectSnapshot[]>([])
  const [loading, setLoading] = useState(true)
  const [confirmingId, setConfirmingId] = useState<number | null>(null)
  const [copiedId, setCopiedId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listProjectSnapshots(clientId)
      .then((data) => { if (!cancelled) setSnapshots(data) })
      .catch(() => { if (!cancelled) setSnapshots([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [clientId])

  async function handleDelete(id: number) {
    await deleteSnapshot(id)
    setSnapshots((prev) => prev.filter((s) => s.id !== id))
    setConfirmingId(null)
  }

  function handleCopyLink(s: ProjectSnapshot) {
    if (!s.shareToken) return
    navigator.clipboard.writeText(`${window.location.origin}/r/${s.slug}/${s.shareToken}`)
    setCopiedId(s.id)
    setTimeout(() => setCopiedId((prev) => (prev === s.id ? null : prev)), 1500)
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center gap-2">
        <Camera size={18} className="text-[#6366F1]" strokeWidth={1.75} />
        <h1 className="text-xl font-semibold text-[#1B2559]">Snapshots</h1>
      </div>

      {loading ? (
        <p className="text-sm text-[#8B8FA7]">Chargement…</p>
      ) : snapshots.length === 0 ? (
        <div className="rounded-xl border border-[#E2E4ED] bg-white p-8 text-center">
          <Camera size={22} className="mx-auto mb-2 text-[#C8CBD8]" strokeWidth={1.5} />
          <p className="text-sm font-medium text-[#1B2559]">Aucun snapshot</p>
          <p className="mt-1 text-xs text-[#8B8FA7]">Génère un snapshot depuis l’éditeur d’un rapport pour le figer et le partager.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#E2E4ED] bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-[#E2E4ED] bg-[#F8F9FC] text-left text-xs uppercase tracking-wide text-[#8B8FA7]">
              <tr>
                <th className="px-4 py-2.5 font-medium">Nom</th>
                <th className="px-4 py-2.5 font-medium">Rapport</th>
                <th className="px-4 py-2.5 font-medium">Période</th>
                <th className="px-4 py-2.5 font-medium">Généré le</th>
                <th className="px-4 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map((s) => (
                <tr key={s.id} className="border-b border-[#EEF0F6] last:border-0">
                  <td className="px-4 py-2.5 font-medium text-[#1B2559]">
                    <div className="flex items-center gap-2">
                      <span>{s.name || 'Sans nom'}</span>
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                          s.accessMode === 'public_link'
                            ? 'bg-[#EEF2FF] text-[#6366F1]'
                            : 'bg-[#F4F5FA] text-[#8B8FA7]'
                        }`}
                      >
                        {s.accessMode === 'public_link' ? 'Public' : 'Privé'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-[#555A73]">{s.report.name}</td>
                  <td className="px-4 py-2.5 text-[#555A73]">{s.periodStart} → {s.periodEnd}</td>
                  <td className="px-4 py-2.5 text-[#8B8FA7]">{new Date(s.createdAt).toLocaleDateString('fr-FR')}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/snapshots/${s.id}`}
                        className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-[#6366F1] hover:bg-[#F4F5FA]"
                      >
                        <Eye size={13} strokeWidth={1.75} /> Voir
                      </Link>
                      {s.accessMode === 'public_link' && s.shareToken && (
                        <button
                          type="button"
                          onClick={() => handleCopyLink(s)}
                          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-[#6366F1] hover:bg-[#F4F5FA]"
                        >
                          <Link2 size={13} strokeWidth={1.75} /> {copiedId === s.id ? 'Copié !' : 'Copier le lien'}
                        </button>
                      )}
                      {confirmingId === s.id ? (
                        <button
                          type="button"
                          onClick={() => handleDelete(s.id)}
                          className="rounded-md bg-[#EF4444] px-2 py-1 text-xs font-semibold text-white hover:opacity-90"
                        >
                          Confirmer
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmingId(s.id)}
                          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-[#8B8FA7] hover:bg-[#F4F5FA] hover:text-[#EF4444]"
                        >
                          <Trash2 size={13} strokeWidth={1.75} /> Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
